<template>
  <a-dropdown
    v-model:open="isEditing"
    :trigger="['click']"
    placement="rightTop"
  >
    <div
      class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
      @click.stop="handleEdit"
    >
      <ThemeImage
        :icon="imageNames.EDIT"
        alt="edit_icon"
        class="size-5"
      />
      <span class="text-sm text_color">Edit property</span>
      <ThemeImage
        :icon="imageNames.RIGHT_ARROW"
        alt="arrow_right"
        class="size-3 ml-auto"
      />
    </div>
    <template #overlay>
      <div class="property_sync_edit_dropdown_content">
        <div class="flex flex-col w-[450px] p-4" @click.stop>
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm text_color font-semibold">Edit Property Sync</span>
          </div>

          <SkeletonLoader v-if="isLoading" page="manage_columns" />

          <div v-else>
            <PropertySyncBuilder
              v-model="propertySyncConfig"
              :entity-type="entityType"
              :service="service"
            />

            <div class="flex justify-end gap-2 mt-4">
              <a-button class="secondary_medium" @click.stop="exitEditMode">
                Cancel
              </a-button>
              <a-button
                class="primary_medium"
                :loading="isSaving"
                @click.stop="save"
              >
                Save
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import PropertySyncBuilder from "@/src/components/property_sync_components/PropertySyncBuilder.vue";
import CRMService from "@/src/api/CRMService.js";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
  propertyMeta: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["property-updated", "close"]);

const isEditing = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const propertySyncConfig = ref(null);

const handleEdit = async () => {
  if (!props.isDynamicObject) {
    message.warning("Property sync editing is only available for custom objects");
    return;
  }

  isEditing.value = true;
  await loadPropertySyncDetails();
};

const loadPropertySyncDetails = async () => {
  if (!props.isDynamicObject || props.propertyMeta?.datatype !== "property_sync") {
    return;
  }

  try {
    isLoading.value = true;

    // First check if metadata is already available in propertyMeta
    if (props.propertyMeta?.metadata) {
      propertySyncConfig.value = props.propertyMeta.metadata;
      return;
    }

    const response = await CRMService.getPraticularAttributeDetails(
      props.entityType,
      props.propertyMeta.slug,
      props.service,
    );

    if (response.success && response.attribute?.metadata) {
      propertySyncConfig.value = response.attribute.metadata;
    } else {
      propertySyncConfig.value = null;
    }
  } catch (error) {
    console.error("Error loading property sync details:", error);
    message.error("Failed to load property sync details");
  } finally {
    isLoading.value = false;
  }
};

const exitEditMode = () => {
  isEditing.value = false;
  propertySyncConfig.value = null;
};

const refreshSchemaStore = async () => {
  await store.dispatch("schema/refreshSchema", {
    slug: props.entityType,
    projectId: props.projectId || null,
    service: props.service || null,
  });
};

const save = async () => {
  if (!props.isDynamicObject || !props.entityType || !props.propertyMeta?.slug) {
    message.error("Missing required data for property sync update");
    return;
  }

  if (
    !propertySyncConfig.value?.from_object_id ||
    !propertySyncConfig.value?.from_attribute_id
  ) {
    message.error("Please configure the property sync settings");
    return;
  }

  try {
    isSaving.value = true;
    const payload = {
      ...props.propertyMeta,
      property_sync: propertySyncConfig.value,
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    const response = await CRMService.updateSchemaObjectAttribute(
      props.entityType,
      props.propertyMeta.slug,
      payload,
      props.service,
    );

    if (response.success) {
      await refreshSchemaStore();
      message.success("Property sync updated successfully");

      emit("property-updated", {
        propertyMeta: props.propertyMeta,
        newName: props.propertyMeta.name,
        response: response,
        isPropertySyncChange: true,
      });

      exitEditMode();
      emit("close");
    } else {
      message.error(response.error || "Failed to update property sync");
    }
  } catch (error) {
    console.error("Error updating property sync:", error);
    message.error("Failed to update property sync");
  } finally {
    isSaving.value = false;
  }
};
</script>

<style>
.property_sync_edit_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  width: fit-content;
  max-height: 600px;
  overflow-y: auto;
}
</style>
